import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Subscription, fromEvent, merge } from 'rxjs';
import { throttleTime, catchError, debounceTime, map, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { RuntimeConfigService } from './runtime-config.service';
import { Capacitor } from '@capacitor/core';
import { OktaAuthService } from './okta-auth.service';

export interface UserPresence {
    UserId: number;
    IsOnline: boolean;
    IsVisible: boolean;
    LastActivityUTC: Date;
}

/**
 * Presence heartbeat. Every write here lands as a row update in dbo.UserPresences for every
 * signed-in user, so the goal is the *minimum* number of requests that keeps the server's
 * 2-minute online timeout satisfied:
 *  - one heartbeat per minute while the tab is open,
 *  - one extra request only when visibility/focus actually changes,
 *  - one offline request when the heartbeat stops.
 * User activity (mouse/keyboard/scroll/touch) is tracked locally and carried on the next
 * heartbeat as LastActivityUTC - it must never trigger a request of its own.
 */
@Injectable({
    providedIn: 'root'
})
export class HeartbeatService implements OnDestroy {
    private heartbeatInterval = 60000; // 1 minute
    private regularHeartbeatSub?: Subscription;
    private activitySub?: Subscription;
    private visibilitySub?: Subscription;
    private onlineStatusSubject = new BehaviorSubject<boolean>(true);
    private isVisibleSubject = new BehaviorSubject<boolean>(!document.hidden);
    private lastActivityTime = new Date(); // Track actual last activity time
    private apiUrl: string;
    // <user-status> is mounted on both the main menu and the apps shell, so start/stop are
    // ref-counted: the heartbeat runs while at least one mount is alive, and a route change
    // between them doesn't send an offline/online pair.
    private consumers = 0;

    public onlineStatus$ = this.onlineStatusSubject.asObservable();
    public isVisible$ = this.isVisibleSubject.asObservable();

    constructor(private http: HttpClient, private oktaAuthService: OktaAuthService, private runtimeConfig: RuntimeConfigService) {
        this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/Presence';
        this.lastActivityTime = new Date(); // Initialize with current time

        // Subscribe to config changes to update apiUrl
        this.runtimeConfig.config$.subscribe(config => {
            this.apiUrl = config.apiUrl + '/Presence';
        });
    }

    private get isRunning(): boolean {
        return this.regularHeartbeatSub !== undefined;
    }

    startHeartbeat() {
        this.consumers++;
        if (this.isRunning) {
            return;
        }

        // Initialize visibility state (combine page visibility + window focus)
        const initiallyVisible = !document.hidden && document.hasFocus();
        this.isVisibleSubject.next(initiallyVisible);

        // Send initial presence update
        this.sendPresenceUpdate(true, this.isVisibleSubject.value);

        // 1. Regular heartbeat every minute - the only recurring write
        this.regularHeartbeatSub = interval(this.heartbeatInterval).subscribe(() => {
            this.sendPresenceUpdate(true, this.isVisibleSubject.value);
        });

        // 2. Activity tracking - local only, reported on the next heartbeat
        const activityEvents = merge(
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'keypress'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'touchstart'),
            fromEvent(document, 'click')
        ).pipe(
            throttleTime(1000)
        );

        this.activitySub = activityEvents.subscribe(() => {
            this.lastActivityTime = new Date();
        });

        // 3. Visibility changes - combine page visibility + window focus. focus/blur and
        // visibilitychange fire together (and flap on every click in/out of the window),
        // so settle briefly and only send when the combined state actually changed.
        const visibilityChanges = merge(
            fromEvent(document, 'visibilitychange'),
            fromEvent(window, 'focus'),
            fromEvent(window, 'blur')
        ).pipe(
            debounceTime(500),
            map(() => !document.hidden && document.hasFocus()),
            filter(isVisible => isVisible !== this.isVisibleSubject.value)
        );

        this.visibilitySub = visibilityChanges.subscribe(isVisible => {
            this.lastActivityTime = new Date(); // Update activity time on focus/visibility changes
            this.isVisibleSubject.next(isVisible);
            this.sendPresenceUpdate(true, isVisible);
        });
    }

    stopHeartbeat() {
        this.consumers = Math.max(0, this.consumers - 1);
        if (this.consumers > 0) {
            return;
        }

        const wasRunning = this.isRunning;

        if (this.regularHeartbeatSub) {
            this.regularHeartbeatSub.unsubscribe();
            this.regularHeartbeatSub = undefined;
        }

        if (this.activitySub) {
            this.activitySub.unsubscribe();
            this.activitySub = undefined;
        }

        if (this.visibilitySub) {
            this.visibilitySub.unsubscribe();
            this.visibilitySub = undefined;
        }

        // Send offline status when stopping - only if we were actually online
        if (wasRunning) {
            this.sendPresenceUpdate(false, false);
        }
    }

    getPresence(userId: number) {
        return this.http.get<UserPresence>(`${this.apiUrl}/GetPresenceAsync/${userId}`);
    }

    getPresences(userIds: number[]) {
        const params = { userIds: userIds.join(',') };
        return this.http.get<UserPresence[]>(`${this.apiUrl}/GetPresencesAsync`, { params });
    }

    private sendPresenceUpdate(isOnline: boolean, isVisible: boolean): void {
        const presence: UserPresence = {
            UserId: 0, // Server infers user from token
            IsOnline: isOnline,
            IsVisible: isVisible,
            LastActivityUTC: this.lastActivityTime // Use tracked activity time, not current time
        };

        // only do this for web, we're handling token refresh differently in native platforms
        if(!Capacitor.isNativePlatform())
        {
            this.oktaAuthService.refreshTokenAfterXMins(10).then(() => {
                this.updatePresence(presence, isOnline);
            });
        }
        else 
        {
            // For native platforms, we can directly update presence without checking token
            this.updatePresence(presence, isOnline);
        }
    }

    private updatePresence(presence: UserPresence, isOnline: boolean): void {

        this.http.post(`${this.apiUrl}/UpdatePresenceAsync`, presence).pipe(
            catchError(error => {
                console.error('Presence update failed:', error);
                this.onlineStatusSubject.next(false);
                return of(null);
            })
        ).subscribe(_ => {
            this.onlineStatusSubject.next(isOnline);
        });
    }


    ngOnDestroy() {
        this.consumers = 1;
        this.stopHeartbeat();
    }
}
